import { formatQuantity } from '@/utils/nativeQuantity';
import React, { useMemo } from 'react';
import { FaChartLine, FaInfoCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Button from '@/components/UI/Button';
import DataTable from '@/components/UI/DataTable';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { Entity, TradingStrategy, UserPortfolioPosition } from '@einhaus/project314-types';
import EntityLink from '@/components/Entity/EntityLink';
import OptionLink from '@/components/Entity/OptionLink';
import { renderTransactionOutput } from '@/components/Trade/TradeRenderUtils.tsx';
import TradeLogicV2ExitSummary from '@/components/TradeLogic/TradeLogicV2ExitSummary';
import type { PortfolioWithAccountingFields, PositionTableData } from './types';
import { formatCurrency, isLongOptionPosition, isOpenShortOptionPosition, isOptionPosition, isShortStockPosition } from './helpers';
import { ENTRY_ARORC_DESCRIPTION, formatArorc } from '@/utils/calculateSellToOpenEntryArorc';
import { formatPositionSide, formatUnderlyingBias } from '@/utils/tradeSemantics';
import { useAssetSessionPrices } from '@/hooks/useAssetSessionPrices';
import PositionExtendedHours from './PositionExtendedHours';

interface PositionsCardProps {
    portfolioId: number;
    portfolio: PortfolioWithAccountingFields;
    positions: UserPortfolioPosition[];
    positionsTableData: PositionTableData[];
    entities: Entity[];
    strategies: TradingStrategy[];
    hasOpenShortOptionPositions: boolean;
    canClosePositions: boolean;
    onSellPosition: (positionId: number) => void;
}

const PositionsCard: React.FC<PositionsCardProps> = ({
    portfolioId,
    portfolio,
    positions,
    positionsTableData,
    entities,
    strategies,
    hasOpenShortOptionPositions,
    canClosePositions,
    onSellPosition
}) => {
    const hasOptionPositions = positions.some((position) => isOptionPosition(position));
    const hasOnlyOptionPositions = positions.length > 0 && positions.every((position) => isOptionPosition(position));
    const hasOnlyLongOptionPositions = positions.length > 0 && positions.every((position) => isLongOptionPosition(position));
    const hasShortStockPositions = positions.some((position) => isShortStockPosition(position));
    const hasOnlyOpenShortOptionPositions = positions.length > 0 && positions.every((position) => isOpenShortOptionPosition(position));
    const hasOnlyShortStockPositions = positions.length > 0 && positions.every((position) => isShortStockPosition(position));
    const strategiesById = useMemo(() => new Map(strategies.map((strategy) => [strategy.id, strategy])), [strategies]);
    const positionsById = useMemo(() => new Map(positions.map((position) => [position.id, position])), [positions]);
    const sessionPricesQuery = useAssetSessionPrices(
        entities.filter((entity) => entity.entityType === 'Asset').map((entity) => entity.symbol)
    );
    const sessionPricesByAssetId = useMemo(
        () => new Map(sessionPricesQuery.data?.map((prices) => [prices.assetId, prices])),
        [sessionPricesQuery.data]
    );
    const hasStockUnderlyings = positions.some((position) => position.entityType === 'Asset' && position.derivativeType !== 'Future');

    const hasExitLogicColumn = positions.some((position) => {
        return Boolean(strategiesById.get(position.strategyId)?.tradeLogicV2ExitVersionId);
    });

    const hasRiskCapitalColumn = positionsTableData.some((position) => Math.abs(position.riskCapital) > 0 && !position.isLongOption);
    const quantityLabels = new Set(positionsTableData.map((position) => position.quantityLabel));
    const hasMixedQuantityUnits = quantityLabels.size > 1;
    const quantityHeader = quantityLabels.size === 1 ? [...quantityLabels][0] : 'Quantity';
    const basisHeader = hasOnlyLongOptionPositions ? 'Premium Paid' : hasOnlyShortStockPositions ? 'Short Proceeds' : 'Cost Basis';
    const entryPriceHeader = hasOnlyOptionPositions ? 'Entry Premium' : hasOptionPositions ? 'Entry Price / Premium' : 'Entry Price';
    const currentPriceHeader = hasOnlyOptionPositions
        ? 'Current Premium'
        : hasOptionPositions
          ? 'Current Price / Premium'
          : 'Current Price';

    const marketValueHeader = hasOnlyOpenShortOptionPositions
        ? 'Liability to Close'
        : hasOnlyShortStockPositions
          ? 'Liability to Cover'
          : hasOpenShortOptionPositions || hasShortStockPositions
            ? 'Market Value / Liability'
            : 'Market Value';

    const positionsColumns = useMemo<ColumnDef<PositionTableData>[]>(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                minSize: 50,
                size: 75,
                maxSize: 100
            },
            {
                accessorKey: 'name',
                header: 'Name',
                minSize: 150,
                size: 250,
                maxSize: 400,
                cell: ({ row }) => {
                    const data = row.original;
                    const entity = entities.find((entity) => entity.id === data.entityId && entity.entityType === data.entityType);
                    return (
                        <div>
                            {entity ? (
                                <EntityLink showName showSymbol entity={entity} />
                            ) : (
                                <div className="animate-pulse h-5 w-32 bg-[#2a2a2a] rounded" />
                            )}
                            {data.option && (
                                <>
                                    <br />
                                    <OptionLink
                                        className="text-zinc-400"
                                        description={data.option.description}
                                        entity={{
                                            entityId: data.entityId,
                                            entityType: data.entityType
                                        }}
                                        optionId={data.option.id}
                                    />
                                    <br />
                                    <span>{data.itmState}</span>
                                </>
                            )}
                        </div>
                    );
                }
            },
            {
                id: 'tradingStrategy',
                accessorFn: (position) => {
                    const strategyName = strategiesById.get(position.strategyId)?.name.trim();

                    return strategyName ? `${strategyName} ${position.strategyId}` : `Strategy ${position.strategyId}`;
                },
                header: 'Trading Strategy',
                minSize: 120,
                size: 180,
                maxSize: 300,
                sortFn: (rowA, rowB) => {
                    const strategyA = strategiesById.get(rowA.original.strategyId)?.name.trim();
                    const strategyB = strategiesById.get(rowB.original.strategyId)?.name.trim();

                    if (strategyA && strategyB)
                        return strategyA.localeCompare(strategyB) || rowA.original.strategyId - rowB.original.strategyId;
                    if (strategyA) return -1;
                    if (strategyB) return 1;

                    return rowA.original.strategyId - rowB.original.strategyId;
                },
                cell: ({ row }) => {
                    const { strategyId } = row.original;
                    const strategyName = strategiesById.get(strategyId)?.name.trim();

                    return (
                        <Link className="text-primary hover:text-blue-400" to={`/user/tradingStrategy/${strategyId}`}>
                            {strategyName || 'Strategy'}
                            <span className="block font-mono text-xs text-zinc-400">#{strategyId}</span>
                        </Link>
                    );
                }
            },
            ...(hasOptionPositions
                ? [
                      {
                          accessorKey: 'optionBuyingStrategy',
                          header: 'Option Strategy',
                          minSize: 100,
                          size: 150,
                          maxSize: 250
                      } satisfies ColumnDef<PositionTableData>
                  ]
                : []),
            {
                accessorKey: 'direction',
                header: 'Position side',
                minSize: 85,
                size: 100,
                maxSize: 130,
                cell: ({ row }) => formatPositionSide(row.original.direction)
            },
            {
                accessorKey: 'underlyingBias',
                header: 'Underlying exposure',
                minSize: 115,
                size: 145,
                maxSize: 190,
                cell: ({ row }) => formatUnderlyingBias(row.original.underlyingBias)
            },
            ...(hasExitLogicColumn
                ? [
                      {
                          accessorKey: 'exitLogic',
                          header: 'Exit Rules',
                          minSize: 170,
                          size: 240,
                          maxSize: 320,
                          cell: ({ row }: { row: { original: PositionTableData } }) => {
                              const position = positions.find((p) => p.id === row.original.id);
                              const strategy = position ? strategiesById.get(position.strategyId) : undefined;
                              const exitCandidateId = position?.exitCandidateId ?? null;
                              const exitCandidateHash = position?.exitCandidateHash ?? null;

                              return (
                                  <TradeLogicV2ExitSummary
                                      className="p-2 bg-transparent"
                                      direction={position?.direction}
                                      exitCandidateHash={exitCandidateHash}
                                      exitCandidateId={exitCandidateId}
                                      showCandidateInfo={false}
                                      showDisplay={false}
                                      strategy={strategy}
                                  />
                              );
                          }
                      } satisfies ColumnDef<PositionTableData>
                  ]
                : []),
            {
                accessorKey: 'entryPrice',
                header: entryPriceHeader,
                minSize: 70,
                size: 100,
                maxSize: 150,
                cell: ({ row }) => `$${row.original.entryPrice.toLocaleString('en-US')}`
            },
            ...(hasOpenShortOptionPositions
                ? [
                      {
                          accessorKey: 'entryArorc',
                          header: () => <span title={ENTRY_ARORC_DESCRIPTION}>Entry ARORC</span>,
                          minSize: 80,
                          size: 105,
                          maxSize: 150,
                          cell: ({ row }: { row: { original: PositionTableData } }) => formatArorc(row.original.entryArorc)
                      } satisfies ColumnDef<PositionTableData>
                  ]
                : []),
            {
                accessorKey: 'currentPrice',
                header: currentPriceHeader,
                minSize: 70,
                size: 100,
                maxSize: 150,
                cell: ({ row }) => `$${row.original.currentPrice.toLocaleString('en-US')}`
            },
            {
                accessorKey: 'quantity',
                header: quantityHeader,
                minSize: 60,
                size: 75,
                maxSize: 120,
                cell: ({ row }) => {
                    const formattedQuantity = formatQuantity(row.original.quantity);

                    if (hasMixedQuantityUnits) {
                        return `${formattedQuantity} ${row.original.quantityLabel.toLowerCase()}`;
                    }

                    return formattedQuantity;
                }
            },
            ...(hasStockUnderlyings
                ? [
                      {
                          id: 'extendedHours',
                          header: 'Extended hours',
                          minSize: 210,
                          size: 280,
                          maxSize: 340,
                          cell: ({ row }) => {
                              const position = positionsById.get(row.original.id);
                              return position ? (
                                  <PositionExtendedHours
                                      position={position}
                                      prices={sessionPricesByAssetId.get(position.entityId)}
                                      isLoading={sessionPricesQuery.isLoading}
                                      isError={sessionPricesQuery.isError}
                                  />
                              ) : null;
                          }
                      } satisfies ColumnDef<PositionTableData>
                  ]
                : []),
            ...(!hasOpenShortOptionPositions && !hasShortStockPositions
                ? [
                      {
                          accessorKey: 'sharesValue',
                          header: 'Position Value',
                          minSize: 80,
                          size: 110,
                          maxSize: 160,
                          cell: ({ row }: { row: { original: PositionTableData } }) => formatCurrency(row.original.sharesValue)
                      } satisfies ColumnDef<PositionTableData>
                  ]
                : []),
            ...(!hasOpenShortOptionPositions
                ? [
                      {
                          accessorKey: 'startingValue',
                          header: basisHeader,
                          minSize: 80,
                          size: 110,
                          maxSize: 160,
                          cell: ({ row }: { row: { original: PositionTableData } }) => formatCurrency(row.original.startingValue)
                      } satisfies ColumnDef<PositionTableData>
                  ]
                : []),
            {
                accessorKey: 'marketValue',
                header: marketValueHeader,
                minSize: 80,
                size: 110,
                maxSize: 160,
                cell: ({ row }) => {
                    if (row.original.isShortExposure) {
                        return formatCurrency(Math.abs(row.original.marketValue));
                    }

                    return formatCurrency(row.original.marketValue);
                }
            },
            ...(hasOpenShortOptionPositions
                ? [
                      {
                          accessorKey: 'premiumReceived',
                          header: 'Premium Received',
                          minSize: 95,
                          size: 125,
                          maxSize: 180,
                          cell: ({ row }: { row: { original: PositionTableData } }) =>
                              row.original.premiumReceived === null ? '—' : formatCurrency(row.original.premiumReceived)
                      } satisfies ColumnDef<PositionTableData>
                  ]
                : []),
            ...(hasRiskCapitalColumn
                ? [
                      {
                          accessorKey: 'riskCapital',
                          header: 'Risk Capital',
                          minSize: 80,
                          size: 110,
                          maxSize: 160,
                          cell: ({ row }: { row: { original: PositionTableData } }) => formatCurrency(row.original.riskCapital)
                      } satisfies ColumnDef<PositionTableData>
                  ]
                : []),
            {
                accessorKey: 'totalPnl',
                header: 'Total P&L',
                minSize: 70,
                size: 100,
                maxSize: 150,
                cell: ({ row }) => {
                    const { totalPnl } = row.original;
                    const colorClass = totalPnl > 0 ? 'text-profit-green' : totalPnl < 0 ? 'text-red-500' : 'text-zinc-400';
                    return <span className={colorClass}>${totalPnl.toLocaleString('en-US')}</span>;
                }
            },
            {
                accessorKey: 'percentPriceImpactToday',
                header: '% Price Impact Today',
                minSize: 80,
                size: 100,
                maxSize: 150,
                cell: ({ row }) => {
                    const percentChange = row.original.percentPriceImpactToday;
                    const colorClass = percentChange > 0 ? 'text-profit-green' : percentChange < 0 ? 'text-red-500' : 'text-zinc-400';
                    return <span className={colorClass}>{percentChange}%</span>;
                }
            },
            ...(!hasOpenShortOptionPositions && !hasShortStockPositions
                ? [
                      {
                          accessorKey: 'percentChangeValueToday',
                          header: '% Value Today',
                          minSize: 80,
                          size: 100,
                          maxSize: 150,
                          cell: ({ row }: { row: { original: PositionTableData } }) => {
                              const percentChange = row.original.percentChangeValueToday;

                              const colorClass =
                                  percentChange > 0 ? 'text-profit-green' : percentChange < 0 ? 'text-red-500' : 'text-zinc-400';

                              return <span className={colorClass}>{percentChange}%</span>;
                          }
                      } satisfies ColumnDef<PositionTableData>
                  ]
                : []),
            {
                accessorKey: 'percentChange',
                header: '% Change',
                minSize: 80,
                size: 100,
                maxSize: 150,
                cell: ({ row }) => {
                    const { percentChange } = row.original;
                    const colorClass = percentChange > 0 ? 'text-profit-green' : percentChange < 0 ? 'text-red-500' : 'text-zinc-400';
                    return <span className={colorClass}>{percentChange}%</span>;
                }
            },
            {
                accessorKey: 'date',
                header: 'Date',
                minSize: 80,
                size: 100,
                maxSize: 150
            },
            {
                accessorKey: 'transactions',
                header: 'Transactions',
                minSize: 150,
                size: 250,
                maxSize: 400,
                cell: ({ row }) => {
                    const position = positions.find((p) => p.id === row.original.id);
                    const tradeForPosition = portfolio?.trades?.find((t) => t.id === position?.tradeId);

                    const pendingOrdersForPosition = portfolio?.pendingOrders?.filter((o) => o.tradeId === position?.tradeId);

                    return (
                        <div className="transactionsOutput wrap-break-words" data-id={row.original.id}>
                            {renderTransactionOutput({
                                transactions: tradeForPosition?.transactions ?? [],
                                pendingOrders: pendingOrdersForPosition ?? [],
                                compactMode: true
                            })}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'actions',
                header: 'Actions',
                minSize: 70,
                size: 90,
                maxSize: 120,
                cell: ({ row }) => (
                    <Button
                        disabled={!canClosePositions}
                        size="sm"
                        title={canClosePositions ? undefined : 'This support session does not include close-position permission.'}
                        variant="destructive"
                        onClick={() => onSellPosition(row.original.id)}
                    >
                        Sell
                    </Button>
                )
            }
        ],
        [
            basisHeader,
            canClosePositions,
            currentPriceHeader,
            entities,
            entryPriceHeader,
            hasExitLogicColumn,
            hasOpenShortOptionPositions,
            hasMixedQuantityUnits,
            hasOptionPositions,
            hasRiskCapitalColumn,
            hasShortStockPositions,
            marketValueHeader,
            onSellPosition,
            portfolio?.pendingOrders,
            portfolio?.trades,
            positions,
            quantityHeader,
            strategiesById,
            positionsById,
            sessionPricesByAssetId,
            sessionPricesQuery.isLoading,
            sessionPricesQuery.isError,
            hasStockUnderlyings
        ]
    );

    return (
        <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12">
                <div className="bg-[#1a1a1a] text-white rounded-lg shadow-md">
                    <div className="p-4">
                        <h5 className="text-xl font-medium mb-3">Positions</h5>
                        <div className="text-sm w-full overflow-hidden">
                            {positionsTableData.length > 0 ? (
                                <div className="w-full">
                                    <DataTable allowOverflow sort striped columns={positionsColumns} data={positionsTableData} />
                                </div>
                            ) : (
                                <div className="text-center p-4">
                                    <div className="mb-3">
                                        <FaInfoCircle aria-hidden="true" className="inline-block text-3xl text-blue-400" />
                                    </div>
                                    <h5 className="text-white text-lg mb-2">No Positions</h5>
                                    <p className="text-zinc-400 mb-3">
                                        This portfolio doesn&apos;t have any positions yet. Use the Backtest feature to create and execute
                                        trades.
                                    </p>
                                    <Button
                                        className="mt-2"
                                        icon={<FaChartLine aria-hidden="true" />}
                                        to={`/user/portfolioBacktest/${portfolioId}`}
                                        variant="primary"
                                    >
                                        Run a Backtest
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PositionsCard;
